import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import './MemePropertiesEditor.css';

/**
 * MemePropertiesEditor - Floating panel for editing meme properties:
 * - Timeline Range (startTime to endTime relative to sentence)
 * - Offset Top (%)
 * - Scale
 */
const MemePropertiesEditor = ({
    meme,
    onUpdate,
    onClose,
    sentenceDuration = 3000 // Duration of the sentence in ms
}) => {
    const rangeRef = useRef(null);
    const [isDragging, setIsDragging] = useState(null); // 'start', 'end', or null

    // Default timing to full sentence range
    const startTime = meme?.startTime ?? 0;
    const endTime = meme?.endTime ?? sentenceDuration;

    // Calculate handle positions as percentages
    const startPercent = sentenceDuration > 0 ? (startTime / sentenceDuration) * 100 : 0;
    const endPercent = sentenceDuration > 0 ? (endTime / sentenceDuration) * 100 : 100;

    // Handle drag for range slider - must be before any conditional returns!
    useEffect(() => {
        if (!meme) return; // Guard inside effect

        const handleMouseMove = (e) => {
            if (!isDragging || !rangeRef.current) return;

            const rect = rangeRef.current.getBoundingClientRect();
            const percent = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
            const newTime = (percent / 100) * sentenceDuration;

            if (isDragging === 'start') {
                const newStart = Math.max(0, Math.min(endTime - 100, newTime));
                onUpdate({ ...meme, startTime: newStart, endTime });
            } else if (isDragging === 'end') {
                const newEnd = Math.max(startTime + 100, Math.min(sentenceDuration, newTime));
                onUpdate({ ...meme, startTime, endTime: newEnd });
            }
        };

        const handleMouseUp = () => {
            setIsDragging(null);
        };

        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, startTime, endTime, sentenceDuration, meme, onUpdate]);

    // Early return after all hooks
    if (!meme) return null;

    const handleStartChange = (value) => {
        const newStart = Math.max(0, Math.min(endTime - 100, parseFloat(value) || 0));
        onUpdate({ ...meme, startTime: newStart, endTime });
    };

    const handleEndChange = (value) => {
        const newEnd = Math.max(startTime + 100, Math.min(sentenceDuration, parseFloat(value) || sentenceDuration));
        onUpdate({ ...meme, startTime, endTime: newEnd });
    };

    const handleOffsetChange = (value) => {
        const offsetTop = Math.max(-50, Math.min(50, parseInt(value) || 0));
        onUpdate({ ...meme, offsetTop });
    };

    const handleScaleChange = (value) => {
        const scale = Math.max(0.5, Math.min(2.0, parseFloat(value) || 1.0));
        onUpdate({ ...meme, scale });
    };

    // Handle drag for range slider
    const handleMouseDown = (handle) => (e) => {
        e.preventDefault();
        setIsDragging(handle);
    };

    const formatTime = (ms) => {
        return (ms / 1000).toFixed(1) + 's';
    };

    return (
        <div className="meme-properties-editor">
            <div className="properties-header">
                <span>Meme Properties</span>
                <button className="close-btn" onClick={onClose}>
                    <X size={16} />
                </button>
            </div>

            {/* Preview */}
            <div className="meme-preview-small">
                <img src={meme.url} alt="Meme preview" />
            </div>

            {/* Timeline Range */}
            <div className="property-row timeline-row">
                <div className="timeline-header">
                    <label>Timeline Range</label>
                    <div className="time-inputs">
                        <input
                            type="text"
                            value={formatTime(startTime)}
                            onChange={(e) => {
                                const val = parseFloat(e.target.value) * 1000;
                                if (!isNaN(val)) handleStartChange(val);
                            }}
                            className="time-input"
                        />
                        <input
                            type="text"
                            value={formatTime(endTime)}
                            onChange={(e) => {
                                const val = parseFloat(e.target.value) * 1000;
                                if (!isNaN(val)) handleEndChange(val);
                            }}
                            className="time-input"
                        />
                    </div>
                </div>

                <div className="timeline-range-slider" ref={rangeRef}>
                    {/* Background track */}
                    <div className="range-track" />

                    {/* Selected range */}
                    <div
                        className="range-selected"
                        style={{
                            left: `${startPercent}%`,
                            width: `${endPercent - startPercent}%`
                        }}
                    />

                    {/* Start handle */}
                    <div
                        className="range-handle start-handle"
                        style={{ left: `${startPercent}%` }}
                        onMouseDown={handleMouseDown('start')}
                    />

                    {/* End handle */}
                    <div
                        className="range-handle end-handle"
                        style={{ left: `${endPercent}%` }}
                        onMouseDown={handleMouseDown('end')}
                    />
                </div>

                <div className="timeline-duration">
                    Total Duration: {formatTime(sentenceDuration)}
                </div>
            </div>

            {/* Offset Top */}
            <div className="property-row">
                <label>Offset Top</label>
                <div className="slider-row">
                    <input
                        type="range"
                        value={meme.offsetTop || 0}
                        onChange={(e) => handleOffsetChange(e.target.value)}
                        min={-50}
                        max={50}
                        step={1}
                    />
                    <span className="value">{meme.offsetTop || 0}%</span>
                </div>
            </div>

            {/* Scale */}
            <div className="property-row">
                <label>Scale</label>
                <div className="slider-row">
                    <input
                        type="range"
                        value={meme.scale || 1.0}
                        onChange={(e) => handleScaleChange(e.target.value)}
                        min={0.5}
                        max={2.0}
                        step={0.1}
                    />
                    <span className="value">{(meme.scale || 1.0).toFixed(1)}x</span>
                </div>
            </div>
        </div>
    );
};

export default MemePropertiesEditor;
